import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchDossier, fetchDossierCsv, fetchReportCsv, fetchReportsOverview, saveBlob, type CsvFile } from './api';
import type { DisruptionDossier, ReportExport, ReportFilters, ReportsOverview } from './types';

/** The window overview — a composition over every disruption of the window, so it is cached a minute. */
export function useReportsOverview(filters: ReportFilters) {
  return useQuery<ReportsOverview>({
    queryKey: ['reports', 'overview', filters.from ?? '', filters.to ?? ''],
    queryFn: () => fetchReportsOverview(filters),
    staleTime: 60_000,
  });
}

function useCsvDownload(fetcher: () => Promise<CsvFile>) {
  return useMutation({
    mutationFn: async () => {
      const { blob, filename } = await fetcher();
      saveBlob(blob, filename);
      return filename;
    },
  });
}

export function useReportDownload(kind: ReportExport, filters: ReportFilters) {
  return useCsvDownload(() => fetchReportCsv(kind, filters));
}

/** The dossier is generated ON OPEN and never refetched behind the operator's back (same as the supplier documents). */
export function useDossier(urn: string | undefined, enabled = true) {
  return useQuery<DisruptionDossier>({
    queryKey: ['reports', 'dossier', urn],
    queryFn: () => fetchDossier(urn!),
    enabled: !!urn && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDossierDownload(urn: string) {
  return useCsvDownload(() => fetchDossierCsv(urn));
}
